import { ReactNode } from 'react';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Heading } from '@/common/components/Heading/Heading';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import * as AccordionPrimitive from '@rn-primitives/accordion';
import { useItemContext } from '@rn-primitives/accordion';

export const AccordionItem = ({ question, answer }: TAccordionItemProps) => {
  const { isExpanded } = useItemContext();
  const height = useSharedValue(0);

  const derivedHeight = useDerivedValue(() =>
    withTiming(height.value * Number(isExpanded), {
      duration: 200,
    })
  );
  const animatedStyle = useAnimatedStyle(() => ({
    height: derivedHeight.value,
  }));

  return (
    <Stack>
      <AccordionPrimitive.Trigger className="cursor-pointer py-md">
        <Stack align="center" justify="between" horizontal>
          <Heading level={5} color={isExpanded ? 'neutral30' : 'neutral20'}>
            {question}
          </Heading>
          {isExpanded ? (
            <Icon icon="minus" color="accentPrimary10" />
          ) : (
            <Icon icon="plus" color="accentPrimary10" />
          )}
        </Stack>
      </AccordionPrimitive.Trigger>
      <Animated.View className={styles.animatedViewRoot} style={animatedStyle}>
        <View
          className={styles.animatedViewContent}
          onLayout={(e) => {
            height.value = e.nativeEvent.layout.height;
          }}
        >
          <BodyText className="pb-md">{answer}</BodyText>
        </View>
      </Animated.View>
    </Stack>
  );
};

export type TAccordionItemProps = {
  question: ReactNode;
  answer: ReactNode;
  name?: string;
};

const styles = {
  animatedViewRoot: 'w-full overflow-hidden',
  animatedViewContent: 'absolute flex w-full items-center',
};
